import "./InventoryManagementBoard.css";

import { TooltipItem } from "chart.js";

import { LogoutDarkIcon } from "../../../../assets";
import { getInventoryViewHeading } from "../../../../constants";
import { DeveloperTemplate, UnitTransactionsPanel } from "../../../../templates";
import { formatNumber, inventoryTooltipLabel } from "../../../../utils";
import {
  Column,
  CursorDataGrid,
  InventoryChartKey,
  InventorySelector,
  InvestmentKPI,
  IssuanceStatusFilter,
  LineChart,
  LoadingSpinner,
  MultiSelect,
  Row,
  Select,
  StackedBarChart,
  UserHeader,
} from "../../../../widget";
import { useInventoryManagementBoard } from "./useInventoryManagementBoard";

const InventoryManagementBoard = (): JSX.Element => {
  const {
    hasEnoughDataForScrolling,
    gridOnChange,
    isLoading,
    dropDownData,
    selectedUnitMeasurementData,
    selectedStatuses,
    handleStatusFilter,
    activeSelectOptionText,
    handleChangeSelectOption,
    pageData,
    kpiData,
    columns,
    onTagsFilter,
    initTagsFilterData,
    tagsFilterData,
    tagsFilterSelectedData,
    hasUnits,
    prefix,
    suffix,
    inventorySelectorButtons,
    handleUpdateSelectedUnitMeasurement,
    searchIssuanceTransactionsSortingCriteria,
    searchIssuanceTransactionsColumns,
    isSalesOverTimeSelected,
    salesOverTimeSeries,
    salesOverTimeColours,
    unitTransactionsPanelFilter,
    unitTransactionsPanelProjectSummary,
    handleCloseUnitTransactionsPanel,
    refresh,
    handleRefresh,
    filterExportColumns,
    filterExportRows,
  } = useInventoryManagementBoard();
  return (
    <DeveloperTemplate activeElement="Inventory">
      <div className="InventoryManagementBoard">
        <Row spacingV="ml">
          <Column span={12}>
            <UserHeader />
          </Column>
        </Row>
        <Row spacingV="ll">
          <Column span={12}>
            <div className="HeaderContainer">
              <h1>{selectedUnitMeasurementData === "" ? "" : `${selectedUnitMeasurementData} `}Inventory</h1>
              {dropDownData.length > 0 && (
                <div className="HeaderButtons">
                  <h3>Filter</h3>
                  <Select
                    onChange={(v) => {
                      handleUpdateSelectedUnitMeasurement(v);
                    }}
                    data={dropDownData}
                    value={selectedUnitMeasurementData}
                    className="HeaderSelect HeaderSelect_measurement"
                    disabled={isLoading}
                  />
                  <IssuanceStatusFilter
                    onChange={handleStatusFilter}
                    values={selectedStatuses}
                    className="HeaderSelect"
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>
          </Column>
        </Row>
        <p className="body1 inventory-description">
          This page shows your organisation-wide inventory. You can switch the display by unit type, review and edit
          issued data, and track availability, reservations, and sales across all projects. Inventory can also be
          managed directly within the individual project listings.
        </p>
        <Row spacingV="ml">
          <Column span={4}>
            <InvestmentKPI amount={kpiData.totalAvailableToSell} title="Available to sell" />
          </Column>
          <Column span={4}>
            <InvestmentKPI amount={kpiData.totalReserved} title="Reserved" />
          </Column>
          <Column span={4}>
            <InvestmentKPI amount={kpiData.totalSold} title="Sold" />
          </Column>
        </Row>
        <div className="InventoryDisplayData">
          <div className="HeaderContainer">
            <h2>{getInventoryViewHeading(activeSelectOptionText)}</h2>
            <InventorySelector
              buttons={inventorySelectorButtons}
              activeSelector={activeSelectOptionText}
              onChange={handleChangeSelectOption}
              disabled={isLoading}
            />
          </div>
          <Row spacingV="s">
            <Column span={12}>
              <div className="InventoryChartContainer">
                {isLoading ? (
                  <div className="InventoryChartLoading">
                    <LoadingSpinner type="blue" size="medium" />
                  </div>
                ) : null}
                {!isLoading && isSalesOverTimeSelected && pageData.chartDatasets ? (
                  <LineChart
                    height={400}
                    labels={pageData.chartLabels as string[]}
                    datasets={pageData.chartDatasets}
                    customChartColours={salesOverTimeColours}
                    xAxisTickOffset={-26}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    yAxisTickCallbackFunction={(val: any) => {
                      return `£${formatNumber(val)}`;
                    }}
                    labelCallbackFunction={(context: TooltipItem<"line">) => {
                      const x = context.label ?? (context.parsed && context.parsed.x);
                      const y = context.parsed && context.parsed.y;
                      return `${String(x)} - £${Number(y).toLocaleString()}`;
                    }}
                  />
                ) : null}
                {!isLoading && !(isSalesOverTimeSelected && pageData.chartDatasets) ? (
                  <StackedBarChart
                    xLabels={pageData.chartLabels}
                    data={pageData.chartData ?? []}
                    hasYAxisGridLines
                    hasYAxisTicks
                    showLegend={false}
                    customTooltipLabel={(context) => inventoryTooltipLabel(context, prefix, suffix)}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    yAxisTickCallbackFunction={(val: any) => {
                      return `${prefix ?? ""}${formatNumber(val)}${suffix ?? ""}`;
                    }}
                  />
                ) : null}
                {activeSelectOptionText !== "Revenue per project" && (
                  <InventoryChartKey
                    surface="dashboard"
                    unitMeasurement={selectedUnitMeasurementData}
                    isSalesOverTime={isSalesOverTimeSelected ?? false}
                    salesOverTimeSeries={salesOverTimeSeries}
                    nutrients={pageData.chartKeyNutrients}
                  />
                )}
              </div>
            </Column>
          </Row>
          <div className={`InventoryFilters ${hasUnits ? "" : "NoData"}`}>
            <h3>Filter by</h3>
            <MultiSelect
              label=" "
              className="TagsFilter"
              placeholder="Tags"
              data={isSalesOverTimeSelected ? initTagsFilterData : tagsFilterData}
              onChange={(keys) => onTagsFilter(keys)}
              disabled={isLoading}
            />
          </div>
          <Row spacingV={hasEnoughDataForScrolling ? "3xl" : undefined}>
            <Column span={12}>
              <div className={`InventoryDataGrid ${hasEnoughDataForScrolling ? "Scrollable" : ""}`}>
                <CursorDataGrid
                  key={
                    selectedUnitMeasurementData +
                    activeSelectOptionText +
                    tagsFilterSelectedData +
                    selectedStatuses.join(",")
                  }
                  onChange={gridOnChange}
                  columns={isSalesOverTimeSelected ? searchIssuanceTransactionsColumns : columns}
                  clientSide={false}
                  filterable
                  pagination={{ pageSize: 1000 }}
                  dataIsLoading={isLoading}
                  loadingMessage=""
                  data={[]}
                  noDataMessage="No inventory data to display"
                  exportable
                  exportButtonText="Export table"
                  exportButtonIcon={<LogoutDarkIcon className="ExportButtonIcon" />}
                  exportFileName=""
                  filterExportColumns={filterExportColumns}
                  filterExportRows={filterExportRows}
                  infiniteScroll
                  hasTotalRow={!isSalesOverTimeSelected}
                  totalColumnNames={
                    isSalesOverTimeSelected
                      ? undefined
                      : ["availableUnits", "reservedUnits", "quotedUnits", "soldUnits"]
                  }
                  defaultSortingCriteria={
                    isSalesOverTimeSelected ? searchIssuanceTransactionsSortingCriteria : undefined
                  }
                  refresh={refresh}
                />
              </div>
            </Column>
          </Row>
        </div>
      </div>
      <UnitTransactionsPanel
        filter={unitTransactionsPanelFilter}
        projectSummary={unitTransactionsPanelProjectSummary}
        show={unitTransactionsPanelFilter !== undefined}
        onClose={handleCloseUnitTransactionsPanel}
        handleRefresh={handleRefresh}
      />
    </DeveloperTemplate>
  );
};

export default InventoryManagementBoard;
